import { FormikHelpers, FormikValues } from 'formik';
import React, { useState } from 'react';
import ReactPlayer from 'react-player';
import { mixed, object } from 'yup';

import { TarefaInformacoes } from 'Component/Admin/Tarefa';
import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import BotaoLink from 'Component/BotaoLink';
import Form, { DropZone } from 'Component/Form';
import Icone from 'Component/Icone';
import Tarefa, { UploadDeVideoParaUA } from 'Interface/Tarefas';
import { useAppSelector } from 'Store';
import { VIDEOS } from 'View/constants';
import { useEditarTarefaMutation } from 'View/Service/Default/Tarefas';

interface Props {
  tarefa: Tarefa;
}

const regras1 = object({
  video: mixed()
    .test('tipo', 'Formato não suportado', (valor) => {
      if (valor && valor.type) {
        return VIDEOS.includes(valor.type);
      }

      return true;
    })
    .required('Obrigatório'),
});

const UploadDeVideosParaUA = ({ tarefa }: Props): JSX.Element => {
  const toast = useToast();
  const [etapa, setEtapa] = useState<number>(1);
  const [editarTarefa, { isLoading }] = useEditarTarefaMutation();
  const usuario = useAppSelector((estado) => estado.autenticacao.usuario);

  const calcularProgresso = (tarefa: Tarefa): number => {
    let progresso = 0;
    const fatia = 100 / 1;

    if (tarefa.dados?.video) {
      progresso += fatia;
    }

    progresso = Math.ceil(progresso);

    if (progresso > 100) {
      progresso = 100;
    }

    return progresso;
  };

  const enviarVideo = async (
    dados: UploadDeVideoParaUA,
    form: FormikHelpers<FormikValues>
  ): Promise<void> => {
    try {
      const novosDados = {
        id: tarefa.id,
        dados: { ...tarefa.dados, video: dados.video },
      };
      await editarTarefa({
        ...novosDados,
        progresso: calcularProgresso(novosDados as Tarefa),
      }).unwrap();

      form.resetForm();

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });
    } catch (erro) {
      toast({
        titulo: 'Erro ao salvar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const removerVideo = async (): Promise<void> => {
    try {
      const novosDados = {
        id: tarefa.id,
        dados: { ...tarefa.dados, video: '' },
      };
      await editarTarefa({
        ...novosDados,
        progresso: calcularProgresso(novosDados as Tarefa),
      }).unwrap();
    } catch (erro) {
      toast({
        titulo: 'Erro ao salvar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const finalizar = async (): Promise<void> => {
    try {
      await editarTarefa({
        id: tarefa.id,
        status: 1,
      }).unwrap();

      toast({ conteudo: 'Finalizado com sucesso!', tema: 'success' });
    } catch (erro) {
      toast({
        titulo: 'Erro ao salvar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const validar = async (): Promise<void> => {
    try {
      await editarTarefa({ id: tarefa.id, validado: 1 }).unwrap();

      toast({
        conteudo: 'Validado com sucesso!',
        tema: 'success',
      });
    } catch (erro) {
      toast({
        titulo: 'Erro ao validar',
        conteudo: 'Verifique o registro de log para mais detalhes!',
        tema: 'danger',
      });
    }
  };

  return (
    <>
      <div className="row">
        <div className="col-md-4">
          <ul className="list-group list-group-flush">
            <TarefaInformacoes tarefa={tarefa} />
            {['super', 'admin'].includes(usuario?.permissao || '') && (
              <div className="mt-4">
                <BotaoLink
                  ms
                  tema="primary"
                  texto="Alterar Titulo da Tarefa"
                  className="w-100 shadow mb-3 ms-auto"
                  to={`/admin/tarefas/alterar-titulo/${tarefa.id}`}
                />
              </div>
            )}
            <li
              className={`list-group-item py-3 d-flex justify-content-between align-items-center cursor-pointer border-0 ${etapa == 1
                  ? 'bg-primary text-white rounded shadow-lg'
                  : 'bg-transparent'
                }`}
              onClick={() => setEtapa(1)}
            >
              <div>
                <strong>Upload de versão final do vídeo</strong>
                <small
                  className={`d-block ${etapa == 1 ? 'text-light' : 'text-muted'
                    }`}
                >
                  Faça o upload da aula gravada pelo professor
                </small>
              </div>
              {tarefa.dados?.video ? (
                <Icone
                  nome={etapa == 1 ? 'check-circle-fill' : 'check-circle'}
                  className="text-success fs-3"
                />
              ) : (
                <Icone
                  nome={etapa == 1 ? 'clock-fill' : 'clock'}
                  className="text-warning fs-3"
                />
              )}
            </li>
          </ul>
          {tarefa.progresso == 100 && tarefa.status == 0 && (
            <div className="mt-4">
              <Botao
                lg
                tema="secondary"
                texto="Finalizar tarefa"
                className="w-100 shadow"
                onClick={finalizar}
                confirmacao="Tem certeza que deseja finalizar essa tarefa?"
              />
            </div>
          )}
          {tarefa.status == 1 && tarefa.validado == 0 && (
            <div className="mt-4">
              <Botao
                lg
                tema="success"
                texto="Validar tarefa"
                icone="check-lg"
                className="w-100 shadow"
                onClick={validar}
                confirmacao="Tem certeza que deseja validar essa tarefa?"
              />
            </div>
          )}
        </div>
        <div className="col-md-8 mt-4 mt-sm-0">
          {etapa == 1 && (
            <div className="bg-white shadow-sm p-3 rounded">
              <h3 className="mb-0">Upload de versão final do vídeo</h3>
              <small className="d-block mb-3 text-muted">
                Faça o upload da aula gravada pelo professor. Lembre-se que o
                vídeo deve ter sido editado e aprovado pela supervisão.
              </small>
              <Form
                regras={regras1}
                onSubmit={enviarVideo}
                desabilitado={
                  tarefa.status == 1 && usuario?.id == tarefa.atendente_id
                }
              >
                {tarefa.dados?.video ? (
                  <>
                    <div
                      className="bg-dark rounded mb-3 d-flex justify-content-center align-items-center"
                      style={{ height: 500 }}
                    >
                      <ReactPlayer
                        url={tarefa.dados.video}
                        height="100%"
                        light
                        controls
                      />
                    </div>
                    <Botao
                      lg
                      tema="danger"
                      texto="Enviar outro vídeo"
                      onClick={removerVideo}
                      processando={isLoading}
                    />
                  </>
                ) : (
                  <>
                    <DropZone
                      name="video"
                      ajuda="As extensões permitidas são MP4 ou AVI."
                    />
                    <Botao type="submit" texto="Salvar" />
                  </>
                )}
              </Form>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UploadDeVideosParaUA;
